import { track } from "@/lib/analytics"
import { useEffect, useId, useMemo, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { createCase, listCases, moveSession, previewMove, removeSession } from "../api"
import type { CaseFileListRow, MatterSessionType } from "../contract"
import { Button } from "../ds"
import { usePopover } from "../ds/usePopover"
import { matchOpenMatter, moveCopy } from "./copy"
import "./MatterPicker.css"

export interface MatterPickerProps {
  type: MatterSessionType
  sessionId: string
  currentCaseId: string | null
  onDone: (c: { id: string; title: string } | null) => void
  onClose: () => void
}

/** The Matter being chosen: an existing one by id, or a new one by the name typed. */
type Target = { id: string | null; title: string }

type Step =
  | { name: "pick" }
  | { name: "duplicate"; title: string; existing: string }
  | { name: "loading" }
  | { name: "confirm"; kind: "in" | "out"; target: Target; copy: string }

const FAILED = "Couldn't move this. Try again."

/** Search the Matters, or type a name to create one; moving always asks first, with the exact consequence. */
export function MatterPicker({ type, sessionId, currentCaseId, onDone, onClose }: MatterPickerProps) {
  const here = useLocation()
  const { open, setOpen, panel } = usePopover()
  const [rows, setRows] = useState<CaseFileListRow[] | null>(null)
  const [query, setQuery] = useState("")
  const [active, setActive] = useState(0)
  const [step, setStep] = useState<Step>({ name: "pick" })
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)
  const id = useId()
  const input = useRef<HTMLInputElement>(null)
  const opened = useRef(false)
  // A Matter made by a Confirm whose move then failed: a retry reuses it instead of creating a second one.
  const created = useRef<{ id: string; title: string } | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const who = type === "nira" ? "This search" : "This draft"

  // usePopover owns Escape and click-outside; the picker is open from the first paint and tells its owner when that ends.
  useEffect(() => { setOpen(true) }, [setOpen])
  useEffect(() => {
    if (open) opened.current = true
    else if (opened.current) onClose()
  }, [open, onClose])

  useEffect(() => {
    let live = true
    listCases().then((r) => { if (live) setRows(r) }, () => { if (live) { setRows([]); setLoadFailed(true); setError("Couldn't load your workspaces. Try again.") } })
    return () => { live = false }
  }, [])

  useEffect(() => { if (step.name === "pick") input.current?.focus(); if (step.name === "confirm") panel.current?.querySelector<HTMLButtonElement>(".nr-mp-actions button")?.focus() }, [step.name, panel])

  const name = query.trim()
  const matches = useMemo(
    () => (rows ?? []).filter((r) => r.id !== currentCaseId && !r.closed && r.title.toLowerCase().includes(name.toLowerCase())),
    [rows, name, currentCaseId],
  )
  // Any typed name can be created; an exact open title is caught by the duplicate warning, not hidden.
  const canCreate = name !== ""
  const optionCount = matches.length + (canCreate ? 1 : 0)
  const cursor = Math.min(active, Math.max(optionCount - 1, 0))

  const choose = async (target: Target) => {
    setError("")
    if (target.id === null) {
      // A new Matter has nothing on its shelf and no id to preview against yet.
      setStep({ name: "confirm", kind: "in", target, copy: `${who} will join ${target.title}, a new workspace; its files join it` })
      return
    }
    setStep({ name: "loading" })
    try {
      const p = await previewMove(target.id, type, sessionId)
      setStep({
        name: "confirm", kind: "in", target,
        copy: moveCopy({ kind: "in", type, matterTitle: target.title, matterFiles: p.matter_files, sessionFiles: p.session_files }),
      })
    } catch {
      setStep({ name: "pick" })
      setError(FAILED)
    }
  }

  const create = () => {
    const hit = matchOpenMatter(name, rows ?? [])
    if (hit) setStep({ name: "duplicate", title: rows!.find((r) => r.id === hit)!.title, existing: hit })
    else void choose({ id: null, title: name })
  }

  const startRemove = async () => {
    if (!currentCaseId) return
    setError("")
    setStep({ name: "loading" })
    try {
      const p = await previewMove(currentCaseId, type, sessionId)
      setStep({
        name: "confirm", kind: "out", target: { id: currentCaseId, title: "" },
        copy: moveCopy({ kind: "out", type, matterTitle: "", matterFiles: p.matter_files, sessionFiles: p.session_files }),
      })
    } catch {
      setStep({ name: "pick" })
      setError(FAILED)
    }
  }

  const confirm = async () => {
    if (step.name !== "confirm" || busy) return
    setBusy(true)
    setError("")
    try {
      if (step.kind === "out") {
        await removeSession(step.target.id!, type, sessionId)
        track("session_moved_to_matter", { type, direction: "out" })
        onDone(null)
      } else {
        let caseId = step.target.id
        if (caseId === null) {
          if (created.current?.title !== step.target.title) {
            created.current = { id: (await createCase({ title: step.target.title })).id, title: step.target.title }
            track("matter_created", { from: "picker" })
          }
          caseId = created.current.id
        }
        await moveSession(caseId, type, sessionId)
        track("session_moved_to_matter", { type, direction: "in" })
        onDone({ id: caseId, title: step.target.title })
      }
    } catch {
      setError(FAILED)
    } finally {
      setBusy(false)
    }
  }

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(Math.min(cursor + 1, optionCount - 1)) }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(Math.max(cursor - 1, 0)) }
    else if (e.key === "Enter" && optionCount > 0) {
      e.preventDefault()
      if (cursor < matches.length) void choose({ id: matches[cursor].id, title: matches[cursor].title })
      else create()
    }
  }

  return (
    <div ref={panel} className="nr nr-mp" role="dialog" aria-label="Choose a workspace" aria-busy={busy || step.name === "loading"}>
      {step.name === "pick" ? (
        <>
          <input
            ref={input}
            className="nr-input nr-mp-search"
            role="combobox"
            aria-expanded="true"
            aria-controls={`${id}-list`}
            aria-activedescendant={optionCount ? `${id}-o${cursor}` : undefined}
            aria-autocomplete="list"
            placeholder="Search workspaces or type a new name"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0) }}
            onKeyDown={onKey}
          />
          {rows === null ? <p className="nr-mp-empty" role="status">Loading your workspaces</p> : null}
          {rows !== null && !loadFailed && optionCount === 0 ? <p className="nr-mp-empty">No workspaces yet. Type a name to create one.</p> : null}
          <ul className="nr-mp-list" id={`${id}-list`} role="listbox" aria-label="Workspaces" aria-busy={rows === null}>
            {matches.map((r, i) => (
              <li
                key={r.id}
                id={`${id}-o${i}`}
                role="option"
                aria-selected={i === cursor}
                className="nr-mp-opt"
                data-active={i === cursor || undefined}
                onMouseEnter={() => setActive(i)}
                onClick={() => void choose({ id: r.id, title: r.title })}
              >
                <span className="nr-mp-title">{r.title}</span>
              </li>
            ))}
            {canCreate ? (
              <li
                id={`${id}-o${matches.length}`}
                role="option"
                aria-selected={cursor === matches.length}
                aria-label={`Create "${name}"`}
                className="nr-mp-opt nr-mp-create"
                data-active={cursor === matches.length || undefined}
                onMouseEnter={() => setActive(matches.length)}
                onClick={create}
              >
                <span>Create &ldquo;<span className="nr-mp-title">{name}</span>&rdquo;</span>
              </li>
            ) : null}
          </ul>
          <Link className="nr-mp-about" to="/workspaces/about" state={{ from: here.pathname + here.search }}>What is a workspace?</Link>
          {currentCaseId ? (
            <div className="nr-mp-foot">
              <Button variant="quiet" size="sm" onClick={() => void startRemove()}>Remove from workspace</Button>
            </div>
          ) : null}
        </>
      ) : null}

      {step.name === "duplicate" ? (
        <div className="nr-mp-step">
          <p className="nr-mp-copy">You already have an open workspace called {step.title}. Use that one?</p>
          <div className="nr-mp-actions">
            {step.existing !== currentCaseId ? (
              <Button variant="primary" size="sm" onClick={() => void choose({ id: step.existing, title: step.title })}>Use existing</Button>
            ) : null}
            <Button variant="secondary" size="sm" onClick={() => void choose({ id: null, title: name })}>Create anyway</Button>
            <Button variant="quiet" size="sm" onClick={() => setStep({ name: "pick" })}>Cancel</Button>
          </div>
        </div>
      ) : null}

      {step.name === "loading" ? <p className="nr-mp-empty" role="status">Checking what moves</p> : null}

      {step.name === "confirm" ? (
        <div className="nr-mp-step">
          <p className="nr-mp-copy">{step.copy}</p>
          <div className="nr-mp-actions">
            <Button variant="primary" size="sm" disabled={busy} onClick={() => void confirm()}>Confirm</Button>
            <Button variant="quiet" size="sm" disabled={busy} onClick={() => { setError(""); setStep({ name: "pick" }) }}>Cancel</Button>
          </div>
        </div>
      ) : null}

      {error ? <p className="nr-mp-error" role="alert">{error}</p> : null}
    </div>
  )
}
